import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSchoolName } from "./schoolNameStore";

/**
 * SETUP:
 *
 * 1) Add the route in your router (e.g. App.js):
 *      <Route path="/login" element={<Login />} />
 *
 * 2) Make sure your attendance route exists too:
 *      <Route path="/attendance" element={<WorkerAttendance />} />
 *
 * 3) Make sure your ID Card route exists too:
 *      <Route path="/id-cards" element={<IdCardGenerator />} />
 *
 * 4) (Optional) Make "/login" your default landing route,
 *    or protect "/attendance" and "/id-cards" so they redirect to
 *    "/login" if not authenticated.
 */

// ── Static credentials ──
// Each entry maps a username/password pair to where it should navigate
// after a successful login. Add more entries here later if needed.
const STATIC_USERS = [
  {
    id: "supervoicer",
    username: "supervoicer",
    password: "123",
    redirectTo: "/worker-attendance-page",
    label: "Supervisor",
    subLabel: "Worker attendance access",
    icon: "fa-user-check",
  },
  {
    id: "idcard",
    username: "idcard",
    password: "id123",
    redirectTo: "/id-cards",
    label: "ID Card",
    subLabel: "ID card generation access",
    icon: "fa-id-card",
  },
];

export default function Login() {
  const schoolName = useSchoolName();
  const navigate = useNavigate();

  const [selectedRole, setSelectedRole] = useState(STATIC_USERS[0]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setUsername("");
    setPassword("");
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!username || !password) {
      setError("Please enter both username and password.");
      return;
    }

    setLoading(true);

    // simulate a brief check so the UI doesn't feel instant/fake
    setTimeout(() => {
      const matchedUser = STATIC_USERS.find(
        (u) => u.username === username && u.password === password
      );

      if (matchedUser) {
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("username", username);
        navigate(matchedUser.redirectTo);
      } else {
        setError("Invalid username or password.");
        setLoading(false);
      }
    }, 500);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@600;700&display=swap');
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css');
        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #F1F5F9; font-family: 'Inter', sans-serif; }
        .login-input { background: rgba(255,255,255,0.08); }
        .login-input:focus { outline: none; border-color: #E8C468 !important; box-shadow: 0 0 0 3px rgba(232,196,104,0.18); }
        .login-btn { transition: all 0.2s ease; cursor: pointer; }
        .login-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); }
        .login-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .eye-btn { cursor: pointer; transition: color 0.15s ease; }
        .eye-btn:hover { color: #E8C468 !important; }
        .gold-gradient-text {
          background: linear-gradient(135deg, #F3D583, #E8C468);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: #E8C468;
        }
        .role-card { cursor: pointer; transition: all 0.2s ease; }
        .role-card:hover { transform: translateY(-1px); border-color: #14532D !important; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shake { 10%, 90% { transform: translateX(-1px); } 20%, 80% { transform: translateX(2px); } 30%, 50%, 70% { transform: translateX(-4px); } 40%, 60% { transform: translateX(4px); } }
        @media (max-width: 860px) {
          .split-login { flex-direction: column !important; }
          .split-login-left, .split-login-right { width: 100% !important; min-height: unset !important; }
        }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          background: "#F1F5F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Inter', sans-serif",
          padding: 20,
        }}
      >
        <div
          className="split-login"
          style={{
            width: "100%",
            maxWidth: 980,
            minHeight: 620,
            display: "flex",
            borderRadius: 24,
            overflow: "hidden",
            boxShadow: "0 24px 70px rgba(0,0,0,0.25)",
            animation: "fadeUp 0.4s ease",
          }}
        >
          {/* LEFT — FORM */}
          <div
            className="split-login-left"
            style={{
              width: "48%",
              minHeight: 620,
              background: "linear-gradient(160deg, #14532D, #166534)",
              padding: "48px 44px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div style={{ marginBottom: 30 }}>
              <div className="gold-gradient-text" style={{ fontWeight: 800, fontSize: 26 }}>
                Welcome back
              </div>
              <div className="gold-gradient-text" style={{ fontSize: 13, marginTop: 6 }}>
                Sign in as <b>{selectedRole.label}</b> to continue
              </div>
            </div>

            {error && (
              <div
                style={{
                  background: "rgba(220,38,38,0.15)",
                  color: "#FCA5A5",
                  border: "1px solid rgba(220,38,38,0.35)",
                  padding: "10px 14px",
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 18,
                  animation: "shake 0.4s ease",
                }}
              >
                <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }}></i>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 18 }}>
                <label
                  className="gold-gradient-text"
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    display: "block",
                    marginBottom: 7,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  {selectedRole.label} ID
                </label>
                <div style={{ position: "relative" }}>
                  <i
                    className="fas fa-user gold-gradient-text"
                    style={{
                      position: "absolute",
                      left: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 13,
                    }}
                  ></i>
                  <input
                    className="login-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    autoComplete="username"
                    style={{
                      width: "100%",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 12,
                      padding: "12px 14px 12px 38px",
                      fontSize: 13,
                      color: "#F0FDF4",
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <label
                  className="gold-gradient-text"
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    display: "block",
                    marginBottom: 7,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  Password
                </label>
                <div style={{ position: "relative" }}>
                  <i
                    className="fas fa-lock gold-gradient-text"
                    style={{
                      position: "absolute",
                      left: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 13,
                    }}
                  ></i>
                  <input
                    className="login-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    style={{
                      width: "100%",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 12,
                      padding: "12px 38px 12px 38px",
                      fontSize: 13,
                      color: "#F0FDF4",
                    }}
                  />
                  <i
                    className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"} eye-btn gold-gradient-text`}
                    onClick={() => setShowPassword((s) => !s)}
                    style={{
                      position: "absolute",
                      right: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 13,
                    }}
                  ></i>
                </div>
              </div>

              <div style={{ textAlign: "right", marginBottom: 22 }}>
               
              </div>

              <button
                type="submit"
                className="login-btn"
                disabled={loading}
                style={{
                  width: "100%",
                  background: "linear-gradient(135deg, #F3D583, #E8C468)",
                  color: "#14532D",
                  border: "none",
                  padding: 14,
                  borderRadius: 14,
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-pulse" style={{ marginRight: 8 }}></i>
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="fas fa-arrow-right-to-bracket" style={{ marginRight: 8 }}></i>
                    Sign In to Portal
                  </>
                )}
              </button>
            </form>

            <div
              style={{
                marginTop: 22,
                fontSize: 11,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 12,
                padding: "10px 14px",
                lineHeight: 1.6,
              }}
            >
              <span className="gold-gradient-text">
                <i className="fas fa-key gold-gradient-text" style={{ marginRight: 6 }}></i>
                Demo: <b>{selectedRole.username}</b> / <b>{selectedRole.password}</b>
                <br />
                Logging in as <b>{selectedRole.label}</b> — {selectedRole.subLabel}
              </span>
            </div>
          </div>

          {/* RIGHT — ROLE SELECTION */}
          <div
            className="split-login-right"
            style={{
              width: "52%",
              minHeight: 620,
              background: "#FBF7EF",
              padding: "48px 44px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "#EDE7D9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 30,
                marginBottom: 18,
              }}
            >
              <i className="fas fa-school" style={{ color: "#14532D" }}></i>
            </div>

            <div
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                fontSize: 32,
                color: "#14532D",
              }}
            >
              {schoolName}
            </div>
            <div style={{ fontSize: 13, color: "#57534E", marginTop: 6 }}>
              School Management System
            </div>

            <div
              style={{
                marginTop: 14,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.8px",
                color: "#78716C",
                background: "#EFEAE0",
                border: "1px solid #E2DDD0",
                borderRadius: 999,
                padding: "6px 16px",
                textTransform: "uppercase",
              }}
            >
              Secure Portal
            </div>

            <div
              style={{
                marginTop: 36,
                width: "100%",
                maxWidth: 380,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.6px",
                color: "#78716C",
                textTransform: "uppercase",
                textAlign: "left",
                marginBottom: 12,
              }}
            >
              Login As
            </div>

            <div style={{ width: "100%", maxWidth: 380, display: "flex", flexDirection: "column", gap: 14 }}>
              {STATIC_USERS.map((role) => {
                const isActive = selectedRole.id === role.id;
                return (
                  <div
                    key={role.id}
                    className="role-card"
                    onClick={() => handleRoleSelect(role)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      textAlign: "left",
                      background: isActive ? "#FFFFFF" : "#F5F1E8",
                      border: isActive ? "2px solid #14532D" : "1px solid #E2DDD0",
                      borderRadius: 16,
                      padding: "16px 18px",
                      boxShadow: isActive ? "0 6px 18px rgba(20,83,45,0.12)" : "none",
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 12,
                        background: isActive ? "#14532D" : "#E2DDD0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className={`fas ${role.icon}`}
                        style={{ color: isActive ? "#FEF9C3" : "#78716C", fontSize: 15 }}
                      ></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#1C1917" }}>
                        {role.label}
                      </div>
                      <div style={{ fontSize: 12, color: "#78716C", marginTop: 2 }}>
                        {role.subLabel}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}